import { useState, useMemo } from "react";
import { CalendarIcon } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

// value: string in "YYYY-MM-DDTHH:mm" (datetime-local format) or ""
// onChange: (newValue) => void
export function DateTimePicker({ value, onChange, testId, placeholder = "Pick date & time" }) {
  const [open, setOpen] = useState(false);

  const dateObj = useMemo(() => (value ? new Date(value) : null), [value]);

  const timeStr = useMemo(() => {
    if (!dateObj || isNaN(dateObj)) return "";
    const hh = String(dateObj.getHours()).padStart(2, "0");
    const mm = String(dateObj.getMinutes()).padStart(2, "0");
    return `${hh}:${mm}`;
  }, [dateObj]);

  const toLocalStr = (d) => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const setDatePart = (d) => {
    if (!d) return;
    const base = dateObj && !isNaN(dateObj) ? new Date(dateObj) : new Date();
    base.setFullYear(d.getFullYear(), d.getMonth(), d.getDate());
    if (!dateObj) { base.setHours(9, 0, 0, 0); }
    onChange(toLocalStr(base));
  };

  const setTimePart = (t) => {
    const [h, m] = (t || "00:00").split(":").map((x) => parseInt(x || "0", 10));
    const base = dateObj && !isNaN(dateObj) ? new Date(dateObj) : new Date();
    base.setHours(h || 0, m || 0, 0, 0);
    onChange(toLocalStr(base));
  };

  const label = dateObj && !isNaN(dateObj)
    ? dateObj.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
    : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          data-testid={testId}
          className={cn("w-full justify-start text-left font-normal", !dateObj && "text-muted-foreground")}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start" data-testid={`${testId}-popover`}>
        <Calendar mode="single" selected={dateObj || undefined} onSelect={setDatePart} initialFocus />
        <div className="p-3 border-t border-border flex items-center gap-2">
          <label className="text-xs text-muted-foreground">Time</label>
          <Input
            type="time"
            value={timeStr}
            onChange={(e) => setTimePart(e.target.value)}
            className="w-32"
            data-testid={`${testId}-time`}
          />
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => { onChange(""); }} data-testid={`${testId}-clear`}>Clear</Button>
            <Button size="sm" onClick={() => setOpen(false)} data-testid={`${testId}-done`}>Done</Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default DateTimePicker;
